import { useContext } from 'react'
import get from 'lodash/get'
import classnames from 'classnames'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { Waypoint } from 'react-waypoint'

// Constants
import {
  ScrolledIntoView,
  TuneNavigateBack,
  InstantOfferNavigateBack,
  EVENTS_LABELS,
  HOME_FACTS_TRACKING_PROGRESS,
} from '../../../constants/trackingEvents'
import { PATH_HELPERS, OVERLAY_HELPERS } from '../../../constants/navigation'
import { ABBREVIATED_MONTH_WITH_YEAR } from '../../../constants/formats'
import { AVM_SOURCE_TYPES } from '../../../constants/valuation'

// Utils
import {
  oneTimeTagEventDispatcher,
  dispatchEvent,
} from '../../../helpers/tagManager'

// Components

import {
  MarketSpotlight,
  MarketSpotlightNavigator,
  MarketSpotlightHeader,
} from '../MarketSpotlight'
import AvmDifferenceWarning from './Chart/Warnings/AvmDifference/AvmDifferenceWarning'
import AvmHotMarketWarning from './Chart/Warnings/AvmHotMarket/AvmHotMarketWarning'
import EquitySmartSearchCalculation from '../MarketSpotlight/EquitySmartSearchTable/EquitySmartSearchCalculation'
import NonDisclosureWarning from './Chart/Warnings/NonDisclosure/NonDisclosureWarning'
import OverlayDefaultHeader from '../Routing/Headers/OverlayDefaultHeader'
import OverlayPanel from '../Routing/OverlayPanel'
import Panel from '../Panel'
import ShouldYouSell from '../MarketSpotlight/ShouldYouSell/ShouldYouSell'
import Text from '../Text'
import ValuationPanelManager from '../../homeowners/Valuation'
import { AppreciationSentence } from './AppreciationSentence'
import Chart from './Chart'
import { EstimatedValueZeroState } from './EstimatedValueZeroState'
import { InstantOffer } from './InstantOffer'
import { NetWorth } from './NetWorthSection'

// Amplitude
import {
  AmplitudeContext,
  AmplitudeEventConstants,
} from '@homebot/shared-util-quartermaster'
import { useAmplitudeDebounceSetters } from '../../../hooks/tracking'

// types
import { CustomerType } from '../../../types/customerType'

// Styles
import styles from './EstimatedValue.module.scss'

// selectors
import { useSelector } from 'react-redux'
import {
  selectCustomerProfile,
  selectCustomerType,
} from '../../../store/selectors/customerProfile'
import { selectClientId } from '../../../store/selectors/client'
import { selectShouldRenderMarketPanel } from '../../../store/selectors/display'
import {
  selectHomeId,
  selectLatestTunedAuthorName,
  selectLatestTunedSource,
} from '../../../store/selectors/home'
import {
  selectHasCMA,
  selectLastCMADate,
} from '../../../store/selectors/home/cma'
import { selectHomeValue } from '../../../store/selectors/home/metadata'
import { selectHomeFactsProgress } from '../../../store/selectors/valuation'
import { RootState } from '../../../types/rootState'
import { EstimatedValuePanelHeading } from './EstimatedValuePanelHeading'
import { EstimatedValueCurrencyDisplay } from './EstimatedValueCurrencyDisplay'
import { Moment } from 'moment'
import { HomeValueEstimateButton } from './HomeValueEstimate/Button'

const scrollIntoView = oneTimeTagEventDispatcher(ScrolledIntoView, {
  eventLabel: EVENTS_LABELS.EstimatedValueModule,
})

const messages = defineMessages({
  you: {
    id: 'EstimatedValue.tune.you',
    defaultMessage: 'you',
  },
})

interface EstimatedValueProps {
  disableValue: boolean
}

export const EstimatedValue = ({ disableValue }: EstimatedValueProps) => {
  const { ampli } = useContext(AmplitudeContext)
  const [trackModuleViewed] = useAmplitudeDebounceSetters(() =>
    ampli?.moduleViewed({
      module_name: AmplitudeEventConstants.AVM,
    })
  )
  const intl = useIntl()
  const customerProfile = useSelector(selectCustomerProfile) || {}
  const customerType = useSelector<RootState, CustomerType>(selectCustomerType)
  const hasCMA = useSelector<RootState, boolean>(selectHasCMA)
  const cmaDate = useSelector<RootState, Moment>(selectLastCMADate)
  const homeValue = useSelector<RootState, number>(selectHomeValue)
  const legacyHomeId = useSelector<RootState, string>(selectHomeId)
  const clientId = useSelector<RootState, string>(selectClientId)
  const latestTuneAuthor = useSelector<RootState, string>(
    selectLatestTunedAuthorName
  )
  const latestTuneSource = useSelector<RootState, 'client' | 'agent'>(
    selectLatestTunedSource
  )
  const homeFactsProgress = useSelector<RootState, number>(
    selectHomeFactsProgress
  )
  const showMarketSpotlight = useSelector<RootState, boolean>(
    selectShouldRenderMarketPanel
  )

  const handleTuneOverlayExit = () => {
    const lastStage = get(
      HOME_FACTS_TRACKING_PROGRESS,
      homeFactsProgress,
      'Ruler'
    )
    const eventLabel = `Last: ${lastStage}`

    dispatchEvent(TuneNavigateBack, {
      eventLabel,
      customerType,
    })
  }

  const handleInstantOfferOverlayExit = () => {
    const lastStage = get(
      HOME_FACTS_TRACKING_PROGRESS,
      homeFactsProgress,
      'Video'
    )
    const eventLabel = `Last: ${lastStage}`

    dispatchEvent(InstantOfferNavigateBack, {
      eventLabel,
      customerType,
    })
  }

  const handleTrackModuleViewed = () => {
    scrollIntoView()
    trackModuleViewed()
  }

  const activePath =
    !!legacyHomeId &&
    !!clientId &&
    PATH_HELPERS.home.buildPath(clientId, legacyHomeId) + window.location.search

  if (!homeValue) {
    return <EstimatedValueZeroState customerProfile={customerProfile} />
  }

  return (
    <Panel data-qa='EstimatedValueCard' className={styles.container}>
      <EstimatedValuePanelHeading homeValue={homeValue} />
      <section className={styles.section}>
        <div>
          <div className={styles.avm}>
            <EstimatedValueCurrencyDisplay
              homeValue={homeValue}
              shouldBlur={false}
            />
          </div>

          {hasCMA && (
            <div className={styles.cmaTuned}>
              <Text variant='neutralCool' variantCount='700' size='s'>
                <FormattedMessage
                  id='Homeowner.estimatedValue.cmaVerifiedMessage.tune'
                  defaultMessage='Tuned by {name} {monthYear}'
                  values={{
                    monthYear: cmaDate.format(ABBREVIATED_MONTH_WITH_YEAR),
                    name:
                      latestTuneSource === AVM_SOURCE_TYPES.CLIENT
                        ? intl.formatMessage(messages.you)
                        : latestTuneAuthor,
                  }}
                />
              </Text>
            </div>
          )}
        </div>
        <OverlayPanel
          className={styles.valueOverlay}
          back={OVERLAY_HELPERS.instantOffer.getOrigin()}
          path={OVERLAY_HELPERS.instantOffer.buildPath()}
          title={OVERLAY_HELPERS.getTitle('instantOffer', intl.formatMessage)}
          anchorOffset={250}
          onClose={handleInstantOfferOverlayExit}
          component={InstantOffer}
          renderHeader={props => (
            <OverlayDefaultHeader {...props} progress={homeFactsProgress} />
          )}
        />
        <OverlayPanel
          className={styles.valueOverlay}
          back={OVERLAY_HELPERS.tuneYourValue.getOrigin()}
          path={OVERLAY_HELPERS.tuneYourValue.buildPath()}
          title={OVERLAY_HELPERS.getTitle('tuneYourValue', intl.formatMessage)}
          anchorOffset={250}
          onClose={handleTuneOverlayExit}
          component={ValuationPanelManager}
          renderHeader={props => (
            <OverlayDefaultHeader {...props} progress={homeFactsProgress} />
          )}
        />
        <Chart withCurrentHomeData clipPath={activePath} />
        <Waypoint onEnter={handleTrackModuleViewed} bottomOffset='25%'>
          <div />
        </Waypoint>
        <AppreciationSentence
          withCurrentHomeData={true}
          className={styles.appreciationText}
        />
        <HomeValueEstimateButton />
        {showMarketSpotlight && (
          <>
            <MarketSpotlightNavigator locked={false} />
            <OverlayPanel
              className={classnames(
                styles.valueOverlay,
                styles.overlayOverflow
              )}
              back={OVERLAY_HELPERS.marketSpotlight.getOrigin()}
              path={OVERLAY_HELPERS.marketSpotlight.buildPath()}
              title={OVERLAY_HELPERS.getTitle(
                'marketSpotlight',
                intl.formatMessage
              )}
              anchorOffset={250}
              component={MarketSpotlight}
              renderHeader={props => <MarketSpotlightHeader {...props} />}
            />
            <OverlayPanel
              back={OVERLAY_HELPERS.equitySmartSearch.getOrigin()}
              path={OVERLAY_HELPERS.equitySmartSearch.buildPath()}
              title={OVERLAY_HELPERS.getTitle(
                'equitySmartSearch',
                intl.formatMessage
              )}
              anchorOffset={250}
              component={EquitySmartSearchCalculation}
            />
          </>
        )}

        <OverlayPanel
          back={OVERLAY_HELPERS.avmDiffWarning.getOrigin()}
          path={OVERLAY_HELPERS.avmDiffWarning.buildPath()}
          title={OVERLAY_HELPERS.getTitle('avmDiffWarning', intl.formatMessage)}
          anchorOffset={200}
          component={AvmDifferenceWarning}
        />

        <OverlayPanel
          back={OVERLAY_HELPERS.avmHotMarketWarning.getOrigin()}
          path={OVERLAY_HELPERS.avmHotMarketWarning.buildPath()}
          title={OVERLAY_HELPERS.getTitle(
            'avmHotMarketWarning',
            intl.formatMessage
          )}
          anchorOffset={200}
          component={AvmHotMarketWarning}
        />

        <OverlayPanel
          back={OVERLAY_HELPERS.nonDisclosureWarning.getOrigin()}
          path={OVERLAY_HELPERS.nonDisclosureWarning.buildPath()}
          title={OVERLAY_HELPERS.getTitle(
            'nonDisclosureWarning',
            intl.formatMessage
          )}
          anchorOffset={200}
          component={NonDisclosureWarning}
        />

        <OverlayPanel
          back={OVERLAY_HELPERS.shouldYouSell.getOrigin()}
          path={OVERLAY_HELPERS.shouldYouSell.buildPath()}
          title={OVERLAY_HELPERS.getTitle('shouldYouSell', intl.formatMessage)}
          anchorOffset={200}
          component={ShouldYouSell}
        />
      </section>

      <NetWorth />
    </Panel>
  )
}
